import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import emailjs from '@emailjs/browser';
import './Contact.css';
import { personalInfo, socialLinks, emailjsConfig } from '../config';

const Contact = () => {
  const ref = useRef(null);
  const formRef = useRef(null);
  const show = useInView(ref, { once: true, margin: '-80px' });
  const [data, setData] = useState({ name: '', email: '', message: '' });
  const [status, setStatus] = useState('');
  const [err, setErr] = useState(false);
  const [sending, setSending] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true); setStatus(''); setErr(false);
    try {
      await emailjs.sendForm(emailjsConfig.serviceId, emailjsConfig.templateId, formRef.current, emailjsConfig.publicKey);
      setStatus("Sent — I'll get back to you soon."); setErr(false);
      setData({ name: '', email: '', message: '' });
    } catch { setStatus('Something went wrong. Email me directly.'); setErr(true); }
    finally { setSending(false); setTimeout(() => setStatus(''), 5000); }
  };

  const change = (e) => setData({ ...data, [e.target.name]: e.target.value });

  return (
    <section id="contact" className="contact" ref={ref}>
      <div className="container">
        <motion.div className="section-label" initial={{ opacity: 0, y: 10 }} animate={show ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5 }}>
          <span>Contact</span>
        </motion.div>

        <motion.h2 className="section-heading" initial={{ opacity: 0, y: 20 }} animate={show ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.05 }}>
          Get in touch
        </motion.h2>

        <div className="contact-grid">
          <motion.div className="contact-info" initial={{ opacity: 0, y: 20 }} animate={show ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.1 }}>
            <p className="contact-text">Have a project in mind or want to chat? I'm always open to new opportunities and interesting conversations.</p>
            <a href={`mailto:${personalInfo.email}`} className="contact-email">{personalInfo.email}</a>
            <div className="contact-socials">
              {socialLinks.github && <a href={socialLinks.github} target="_blank" rel="noopener noreferrer">GitHub</a>}
              {socialLinks.linkedin && <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer">LinkedIn</a>}
              {socialLinks.twitter && <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer">X</a>}
            </div>
          </motion.div>

          <motion.div className="contact-form-col" initial={{ opacity: 0, y: 20 }} animate={show ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.5, delay: 0.15 }}>
            <form ref={formRef} onSubmit={submit} className="contact-form">
              <div className="form-row">
                <div className="form-field">
                  <label htmlFor="name">Name</label>
                  <input type="text" id="name" name="name" value={data.name} onChange={change} required disabled={sending} autoComplete="name" />
                </div>
                <div className="form-field">
                  <label htmlFor="email">Email</label>
                  <input type="email" id="email" name="email" value={data.email} onChange={change} required disabled={sending} autoComplete="email" />
                </div>
              </div>
              <div className="form-field">
                <label htmlFor="message">Message</label>
                <textarea id="message" name="message" value={data.message} onChange={change} required rows="5" disabled={sending} />
              </div>
              <button type="submit" className="btn btn-primary submit-btn" disabled={sending}>{sending ? 'Sending...' : 'Send Message'}</button>
              <AnimatePresence>{status && <motion.p className={`form-status ${err ? 'error' : ''}`} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>{status}</motion.p>}</AnimatePresence>
            </form>
          </motion.div>
        </div>

        <footer className="site-footer">
          <div className="footer-line" />
          <div className="footer-row">
            <span>{personalInfo.name}</span>
            <span>&copy; {new Date().getFullYear()}</span>
          </div>
        </footer>
      </div>
    </section>
  );
};

export default Contact;
